import React, { useContext, useState, useEffect } from 'react';
import './faqs.sass';
import { Skeleton, Table, Button, Modal, Form, Input, Tabs } from 'antd';
import { ConfigContext } from '../../hooks/context/config';
import '../../i18n';
import { useTranslation } from 'react-i18next';
import {
    TitleCustom,
    ParagraphCustom,
    PanelsCustom,
    CollapseCustom,
    FontIconsCustom,
} from '../custom/index';
import { RestApiClientContext } from 'src/hooks/context/pageContext';
import { FaqsModel } from 'src/models/custom/faqs';
import { v4 as uuidv4 } from 'uuid';
import Language from 'src/models/language/languagesManagement';
import { faEdit, faTrashAlt } from '@fortawesome/free-solid-svg-icons';

const { TabPane } = Tabs;

export type Faq = {
    id: string;
    title: string;
    description: string;
    language: string;
    order: number;
};

export function Faqs() {
    const { t } = useTranslation(['urls', 'faqs']);
    const infoClient = useContext(ConfigContext);
    const [faqs, setFaqs] = useState<Faq[]>([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingFaq, setEditingFaq] = useState<Faq | null>(null);
    const [form] = Form.useForm();
    const [languages, setLanguages] = useState<Language[]>([]);
    const restApiClient = useContext(RestApiClientContext);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(false);

    const listFaqsBackOffice: FaqsModel[] = [
        {
            header: t('faqs:questions.1'),
            description: t('faqs:answers.1', { 0: '[description]' }),
        },
        {
            header: t('faqs:questions.2'),
            description: t('faqs:answers.2', { 0: '[description]' }),
        },
        { header: t('faqs:questions.3'), description: t('faqs:answers.3') },
        {
            header: t('faqs:questions.4', { 0: infoClient.incentiveSiteName }),
            description: t('faqs:answers.4'),
        },
        {
            header: t('faqs:questions.5', { 0: infoClient.incentiveSiteName }),
            description: t('faqs:answers.5'),
        },
        { header: t('faqs:questions.6'), description: t('faqs:answers.6') },
    ];

    const getLanguages = (): void => {
        restApiClient.getAgentLanguages().then((r) => {
            setLanguages(r);
        });
    };

    const openModal = (faq = null, language) => {
        setEditingFaq(faq);
        form.setFieldsValue(faq || { title: '', description: '', id: uuidv4(), language });
        setModalVisible(true);
    };

    const handleOk = () => {
        setEditing(true);
        form.validateFields().then((values) => {
            if (editingFaq) {
                setFaqs(faqs.map((f) => (f.id === values.id ? { ...f, ...values } : f)));
                restApiClient.updateFaq(values).finally(() => {
                    setModalVisible(false);
                    setEditing(false);
                });
            } else {
                setFaqs([...faqs, { ...values, order: faqs.length + 1 }]);
                setModalVisible(false);
                restApiClient
                    .createFaq(values)
                    .catch(() => {
                        setFaqs(faqs.filter((f) => f.id !== values.id));
                    })
                    .finally(() => setEditing(false));
            }
        });
    };

    useEffect(() => {
        const func = async () => {
            await getLanguages();
            await restApiClient.getFaqs().then((response) => {
                setFaqs(response);
            });
        };
        setLoading(true);
        func().finally(() => setLoading(false));
    }, []);

    const handleDelete = (id) => {
        Modal.confirm({
            title: t('faqs:delete') + ' FAQ',
            content: t('faqs:delete-question'),
            okText: t('faqs:yes'),
            cancelText: t('faqs:no'),
            onOk: () => {
                const faqDeleted = faqs.find((f) => f.id === id);
                setFaqs(faqs.filter((f) => f.id !== id));
                restApiClient.deleteFaq(id).catch(() => setFaqs([...faqs, faqDeleted]));
            },
        });
    };

    const moveFaq = (index, direction) => {
        setEditing(true);
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= faqs.length) return;
        const items = Array.from(faqs);
        [items[index], items[newIndex]] = [items[newIndex], items[index]];
        const updatedFaqs = items.map((f, idx) => ({ ...f, order: idx }));
        setFaqs(updatedFaqs);
        restApiClient.changeOrderFaqs(updatedFaqs).finally(() => setEditing(false));
    };

    return (
        <Skeleton active loading={loading}>
            <div className="container special-mb">
                <div>
                    <TitleCustom level={1} class="title-section" text={t('faqs:main-title')} />
                    <ParagraphCustom style={{ marginBottom: 20 }} text={t('faqs:explained')} />
                </div>
                <div>
                    <TitleCustom level={1} class="title-section" text={t('faqs:wa')} />
                </div>

                <Tabs style={{ marginBottom: 20 }} defaultActiveKey={languages[0]?.id}>
                    {languages.map((lang) => (
                        <TabPane
                            animated
                            tab={
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <img src={lang.imageName} height={15} />
                                    <span>{lang.name}</span>
                                </div>
                            }
                            key={lang.id}
                        >
                            <Button
                                disabled={editing || loading}
                                type="primary"
                                onClick={() => openModal(null, lang.id)}
                                style={{ marginBottom: 20 }}
                            >
                                {t('faqs:create')}
                            </Button>

                            <Table
                                bordered
                                title={() => t('faqs:header')}
                                rowKey="id"
                                dataSource={faqs
                                    .filter((f) => f.language === lang.id)
                                    .sort((a, b) => a.order - b.order)}
                                pagination={false}
                                columns={[
                                    {
                                        title: '#',
                                        render: (_: any, __: Faq, index: number) => index + 1,
                                        width: '4%',
                                        align: 'center',
                                    },
                                    {
                                        title: t('faqs:label-title'),
                                        dataIndex: 'title',
                                        width: '25%',
                                    },
                                    {
                                        title: t('faqs:label-description'),
                                        dataIndex: 'description',
                                    },
                                    {
                                        title: '',
                                        width: '15%',
                                        align: 'center',
                                        render: (_: any, faq: Faq, index: number) => (
                                            <>
                                                <div
                                                    style={{
                                                        justifyContent: 'center',
                                                        flexWrap: 'wrap',
                                                    }}
                                                    className={`actions-cell`}
                                                >
                                                    <FontIconsCustom
                                                        color="grey"
                                                        icon={faEdit}
                                                        onClick={() => openModal(faq, lang.id)}
                                                    />
                                                    <FontIconsCustom
                                                        classIcon={`delete active`}
                                                        icon={faTrashAlt}
                                                        onClick={() => handleDelete(faq.id)}
                                                    />
                                                    <div>
                                                        {index > 0 && (
                                                            <Button
                                                                disabled={editing}
                                                                type="default"
                                                                onClick={() => moveFaq(index, -1)}
                                                            >
                                                                ↑
                                                            </Button>
                                                        )}
                                                        {index < faqs.length - 1 && (
                                                            <Button
                                                                disabled={editing}
                                                                type="default"
                                                                onClick={() => moveFaq(index, 1)}
                                                            >
                                                                ↓
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            </>
                                        ),
                                    },
                                ]}
                            />

                            <Modal
                                title={
                                    editingFaq ? t('faqs:edit') + ' FAQ' : t('faqs:create') + ' FAQ'
                                }
                                visible={modalVisible}
                                onOk={handleOk}
                                onCancel={() => setModalVisible(false)}
                                okText={t('faqs:button-save')}
                            >
                                <Form form={form} layout="vertical">
                                    <Form.Item
                                        name="title"
                                        label={t('faqs:label-title')}
                                        rules={[
                                            {
                                                required: true,
                                                message: t('faqs:message-title-required'),
                                            },
                                        ]}
                                    >
                                        <Input />
                                    </Form.Item>
                                    <Form.Item
                                        name="description"
                                        label={t('faqs:label-description')}
                                        rules={[
                                            {
                                                required: true,
                                                message: t('faqs:message-description-required'),
                                            },
                                        ]}
                                    >
                                        <Input.TextArea rows={4} />
                                    </Form.Item>
                                    <Form.Item hidden name="language" label="Language">
                                        <Input />
                                    </Form.Item>
                                    <Form.Item hidden name="id" label="id">
                                        <Input />
                                    </Form.Item>
                                </Form>
                            </Modal>
                        </TabPane>
                    ))}
                </Tabs>

                <div
                    style={{
                        justifyContent: 'space-between',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <TitleCustom level={1} class="title-section" text={t('faqs:bo')} />
                </div>

                <CollapseCustom className="black-style" bordered={false}>
                    {listFaqsBackOffice.map((e, i) => (
                        <PanelsCustom header={e.header} key={i}>
                            <ParagraphCustom text={e.description} />
                        </PanelsCustom>
                    ))}
                </CollapseCustom>
            </div>
        </Skeleton>
    );
}
